import _objectDestructuringEmpty from "@babel/runtime/helpers/objectDestructuringEmpty";
import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { IntlMessagesProvider } from '@atlaskit/intl-messages-provider';
import { CreateField, CreateForm, SiteSelect, TextField, useLinkCreateCallback } from '@atlaskit/link-create';
import { fg } from '@atlaskit/platform-feature-flags';
import SectionMessage from '@atlaskit/section-message';
import { DateRangePicker } from '@atlassian/date-range-picker/date-range-picker';
import { Fields, testIds } from '../../common/constants';
import { GOAL_FORM } from '../../common/utils/analytics/constants';
import { InvalidGoalError, InvalidRelationshipError, UserPermissionError } from '../../common/utils/errors';
import { fetchMessagesForLocale } from '../../common/utils/locale/fetch-messages-for-locale';
import { createGoal } from '../../services/create-goal';
import { getDefaultAtlasBaseURL } from '../../services/create-goal/utils';
import { GoalTypeSelect } from './goal-type-select';
import { messages } from './messages';
import { UserPickerForSelectedSite } from './user-picker-for-selected-site';
export var PACKAGE_DATA = {
  packageName: "@atlassian/link-create-goal" || '',
  packageVersion: "3.5.0" || '',
  componentName: GOAL_FORM
};
var initialSelectedSite = function initialSelectedSite(sites, defaultCloudId) {
  var _sites$find;
  if (!sites) {
    return null;
  }
  return (_sites$find = sites.find(function (site) {
    return site.value.cloudId === defaultCloudId;
  })) !== null && _sites$find !== void 0 ? _sites$find : sites[0];
};
var jiraConnectExpandUrl = "".concat(getDefaultAtlasBaseURL(), "/your-work?startTrialProduct=townsquare&edition=free&sourceContext=atlas-jira-connect&targetTab=same&startTrialCloudId");
var adminHubUrl = getDefaultAtlasBaseURL('admin');
var GoalForm = function GoalForm(_ref) {
  var _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? testIds.form : _ref$testId,
    defaultCloudId = _ref.defaultCloudId,
    _ref$defaultFormValue = _ref.defaultFormValues,
    defaultFormValues = _ref$defaultFormValue === void 0 ? {} : _ref$defaultFormValue,
    defaultOwner = _ref.defaultOwner,
    gatewayBaseUrl = _ref.gatewayBaseUrl,
    isOrgAdmin = _ref.isOrgAdmin,
    orgId = _ref.orgId,
    availableSites = _ref.availableSites,
    parentGoalAri = _ref.parentGoalAri;
  var intl = useIntl();
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    goalCreationError = _useState2[0],
    setGoalCreationError = _useState2[1];
  var defaultName = defaultFormValues.name;
  // eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
  var isGoalTypesFeEnabled = fg('atlas_goal_types_enabled_ui');
  // eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-registration
  var isDatePickerEnabled = fg('atlas_date_picker_enabled_ui');
  var _useState3 = useState(undefined),
    _useState4 = _slicedToArray(_useState3, 2),
    targetDateSelected = _useState4[0],
    setTargetDateSelected = _useState4[1];
  var siteOptions = useMemo(function () {
    if (!availableSites) {
      return;
    }
    return availableSites.map(function (site) {
      return {
        label: site.displayName,
        value: {
          avatarUrl: site.avatarUrl,
          cloudId: site.cloudId,
          url: site.url
        }
      };
    });
  }, [availableSites]);

  // Will hide site selector if only same site as the current site is available
  var hasSingleMatchingSite = !siteOptions || siteOptions.length === 1 && siteOptions[0].value.cloudId === defaultCloudId;
  var initialValues = {
    name: defaultName !== null && defaultName !== void 0 ? defaultName : undefined,
    cloudId: defaultCloudId,
    owner: defaultOwner,
    site: initialSelectedSite(siteOptions, defaultCloudId)
  };

  // Requests townsquare access from org admin
  var nonAdminRedirect = defaultCloudId ? "".concat(jiraConnectExpandUrl, "&startTrialCloudId=").concat(defaultCloudId) : jiraConnectExpandUrl;

  // If org admin: redirected to adminhub to opt in for townsquare
  var signUpRedirectionLink = isOrgAdmin && orgId && defaultCloudId ? "".concat(adminHubUrl, "/o/").concat(orgId, "/townsquare?cloudId=").concat(defaultCloudId, "&isSignup=true") : nonAdminRedirect;
  var _useLinkCreateCallbac = useLinkCreateCallback(),
    onCreate = _useLinkCreateCallbac.onCreate,
    onFailure = _useLinkCreateCallbac.onFailure,
    onCancel = _useLinkCreateCallbac.onCancel;
  var nameValidator = useMemo(function () {
    return {
      isValid: function isValid(val) {
        return !!val;
      },
      errorMessage: intl.formatMessage(messages.nameValidationError)
    };
  }, [intl]);
  var ownerValidator = useMemo(function () {
    return {
      isValid: function isValid(val) {
        return !!val;
      },
      errorMessage: intl.formatMessage(messages.ownerValidationError)
    };
  }, [intl]);
  var handleSubmit = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(formData) {
      var _site$value$cloudId;
      var name, site, cloudId, owner, goalTypeSelect, targetDate, ownerId, goalTypeAri, siteSelectedId, goal;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            name = formData.name, site = formData.site, cloudId = formData.cloudId, owner = formData.owner, goalTypeSelect = formData.goalTypeSelect, targetDate = formData.targetDate;
            ownerId = owner === null || owner === void 0 ? void 0 : owner.id;
            if (!(!name || !cloudId || !ownerId)) {
              _context.next = 4;
              break;
            }
            return _context.abrupt("return");
          case 4:
            goalTypeAri = goalTypeSelect === null || goalTypeSelect === void 0 ? void 0 : goalTypeSelect.value;
            /**
             * site will not be in form data if site selection isn't enabled, in which
             * case we fall back to site url derived from the `cloudId` prop.
             */
            siteSelectedId = (_site$value$cloudId = site === null || site === void 0 ? void 0 : site.value.cloudId) !== null && _site$value$cloudId !== void 0 ? _site$value$cloudId : cloudId;
            _context.prev = 6;
            _context.next = 9;
            return createGoal({
              name: name,
              cloudId: siteSelectedId,
              gatewayBaseUrl: gatewayBaseUrl,
              ownerId: ownerId,
              goalTypeAri: goalTypeAri,
              targetDate: targetDate,
              parentGoalAri: parentGoalAri
            });
          case 9:
            goal = _context.sent;
            if (!(goal && onCreate)) {
              _context.next = 13;
              break;
            }
            _context.next = 13;
            return onCreate({
              ari: goal.id,
              objectId: goal.id,
              url: goal.url || '#',
              objectType: 'goal',
              data: {
                name: name,
                iconData: goal.iconData,
                ownerId: ownerId,
                key: goal.key
              }
            });
          case 13:
            _context.next = 20;
            break;
          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](6);
            if (_context.t0 instanceof Error) {
              setGoalCreationError(_context.t0);
            }
            if (!onFailure) {
              _context.next = 20;
              break;
            }
            return _context.abrupt("return", onFailure(_context.t0));
          case 20:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[6, 15]]);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [gatewayBaseUrl, onCreate, onFailure, parentGoalAri]);
  return /*#__PURE__*/React.createElement(CreateForm, {
    isLoading: false,
    testId: testId,
    onCancel: onCancel,
    onSubmit: handleSubmit,
    initialValues: initialValues
  }, /*#__PURE__*/React.createElement(TextField, {
    isRequired: true,
    name: Fields.Name,
    testId: testIds.name,
    label: intl.formatMessage(messages.nameLabel),
    placeholder: intl.formatMessage(messages.namePlaceholder),
    validators: [nameValidator],
    autoFocus: true,
    maxLength: 255
  }), isGoalTypesFeEnabled && /*#__PURE__*/React.createElement(GoalTypeSelect, {
    defaultCloudId: defaultCloudId,
    gatewayBaseUrl: gatewayBaseUrl
  }), !hasSingleMatchingSite && /*#__PURE__*/React.createElement(SiteSelect, {
    options: siteOptions,
    testId: "link-create-jira-site-picker"
  }), isDatePickerEnabled && /*#__PURE__*/React.createElement(CreateField, {
    testId: "link-create-field-target-date",
    name: 'targetDate',
    label: intl.formatMessage(messages.goalDateLabel)
  }, function (_ref3) {
    var fieldProps = _extends({}, (_objectDestructuringEmpty(_ref3), _ref3));
    return /*#__PURE__*/React.createElement(DateRangePicker, {
      placeholder: intl.formatMessage(messages.goalDatePlaceholder),
      value: targetDateSelected === null || targetDateSelected === void 0 ? void 0 : targetDateSelected.date,
      onChange: function onChange(value, confidence) {
        fieldProps.onChange({
          date: value,
          confidence: confidence
        });
        setTargetDateSelected({
          date: value,
          confidence: confidence
        });
      }
    });
  }), /*#__PURE__*/React.createElement(UserPickerForSelectedSite, {
    defaultValue: defaultOwner,
    name: "owner",
    label: intl.formatMessage(messages.goalOwnerLabel),
    placeholder: intl.formatMessage(messages.goalOwnerPickerPlaceholder),
    productKey: "people",
    defaultCloudId: defaultCloudId,
    validators: [ownerValidator]
  }), goalCreationError && /*#__PURE__*/React.createElement(SectionMessage, {
    title: intl.formatMessage(messages.goalCreationError),
    appearance: "error"
  }, /*#__PURE__*/React.createElement(GoalCreationErrorMessage, {
    error: goalCreationError,
    signUpRedirectionLink: signUpRedirectionLink
  })));
};
export var GoalCreationErrorMessage = function GoalCreationErrorMessage(_ref4) {
  var error = _ref4.error,
    signUpRedirectionLink = _ref4.signUpRedirectionLink;
  switch (true) {
    case error instanceof UserPermissionError:
      return /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "linkCreate.CreateGoal.create.no-access.error",
        defaultMessage: "Looks like you don't have access to create Goals. You can request access by visiting {link}",
        description: "Error displayed when attempting to create a goal when user does not have the required permissions"
        // eslint-disable-next-line @atlaskit/design-system/no-html-anchor
        ,
        values: {
          link: /*#__PURE__*/React.createElement("a", {
            href: signUpRedirectionLink
          }, "this link")
        }
      });
    case error instanceof InvalidGoalError:
      return /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "linkCreate.CreateGoal.create.invalid.error",
        defaultMessage: "Looks like you're trying to link something that isn't a goal",
        description: "Error displayed when attempting to create a goal when user is trying to link something that isn't a goal"
      });
    case error instanceof InvalidRelationshipError:
      return /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "linkCreate.CreateGoal.create.invalid-relationship.error",
        defaultMessage: "Looks like you're trying to link something that we don't support. Currently we only support linking goals with Jira issues",
        description: "Error displayed when attempting to create a goal when user is trying to link a goal to something we don't support"
      });
    default:
      return /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "linkCreate.CreateGoal.create.network.error",
        defaultMessage: "Something went wrong, please check your internet connection and try again",
        description: "Error displayed when a server request fails"
      });
  }
};
export default function ComposedGoalForm(props) {
  return /*#__PURE__*/React.createElement(AnalyticsContext, {
    data: PACKAGE_DATA
  }, /*#__PURE__*/React.createElement(IntlMessagesProvider, {
    loaderFn: fetchMessagesForLocale
  }, /*#__PURE__*/React.createElement(GoalForm, props)));
}